/**
 * The works picker's controller: `workPicker.model.ts` wired to
 * `searchPublishableWorks`, with thumbnails from `useWorkThumbnails`.
 *
 * It owns the typed query, the debounced search key (query and offset as one
 * object, so an offset can never belong to some other query), the last page
 * of results, and a failed flag. It does NOT own whether the dialog is open —
 * that sits on the rooms stage in `useViewingRoomsController.ts` — and every
 * effect here returns early while closed, so the dialog can stay mounted and
 * hand focus back to the button that opened it.
 */

import { useEffect, useState } from "react";

import { searchPublishableWorks } from "../lib/api.ts";
import type { Choice } from "../lib/choices.ts";
import { useWorkThumbnails } from "../lib/useWorkThumbnails.ts";
import { DEBOUNCE_MS, PAGE_SIZE } from "./workPicker.constants.ts";
import { nextOffset, prevOffset, resolvePicker } from "./workPicker.model.ts";
import type { Results, SearchKey, WorkPickerView } from "./workPicker.model.ts";

export interface WorkPickerController {
  view: WorkPickerView;
  typeQuery: (value: string) => void;
  goPrev: () => void;
  goNext: () => void;
}

/**
 * Browsing the works a collector may publish, one page at a time.
 *
 * Separate from `useViewingRoomsController` because a search query, a page
 * offset and a set of signed thumbnail URLs are meaningless on the rooms list —
 * and that controller is already long. What it does NOT own is whether the
 * dialog is open: the button that opens it lives in the create form, and views
 * hold no state, so `picking` belongs on the room stage beside everything else
 * that decides what this screen shows.
 *
 * Every effect below opens `if (!open) return`, so the dialog can stay mounted
 * and idle — which it must, because the browser returns focus to the invoking
 * element on `close()` and unmounting instead would lose it.
 */
export function useWorkPickerController(
  open: boolean,
  selected: Choice[],
): WorkPickerController {
  /** What is in the box. Immediate, so the input never lags a keystroke. */
  const [typed, setTyped] = useState("");
  /**
   * What is being fetched. Debounced, and ONE object rather than two states.
   *
   * As separate states, a query change that left the offset at 240 would fetch
   * an empty page and the modal would look broken under a perfectly valid
   * search box. Bundled, "this offset belongs to some other query" is not
   * representable.
   */
  const [search, setSearch] = useState<SearchKey>({ query: "", offset: 0 });
  const [results, setResults] = useState<Results | null>(null);
  const [failed, setFailed] = useState(false);
  const images = useWorkThumbnails(open && results !== null ? results.rows : null, (work) => work);

  // The debounce, as an effect with a cleanup rather than a ref holding a timer:
  // it self-cancels on re-run and on unmount, so there is nothing to remember to
  // clear. A new query always returns to the first page.
  useEffect(() => {
    if (!open) return;

    const timer = setTimeout(() => {
      setSearch((current) =>
        current.query === typed.trim() ? current : { query: typed.trim(), offset: 0 },
      );
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [typed, open]);

  useEffect(() => {
    if (!open) return;
    let active = true;

    searchPublishableWorks(search.query, PAGE_SIZE, search.offset)
      .then((response) => {
        if (!active) return;
        setFailed(false);
        // Stored WITH the search it answers. `resolvePicker` compares that key
        // against the live one at render time, so a response for a superseded
        // query is discarded by derivation — the `active` guard below defends a
        // different moment, and both are wanted.
        setResults({ key: search, rows: response.works, total: response.total });
      })
      .catch(() => {
        if (active) setFailed(true);
      });

    return () => {
      active = false;
    };
  }, [open, search]);

  return {
    view: resolvePicker({ open, typed, search, results, failed, images, selected }),

    typeQuery: setTyped,

    goPrev: () => {
      setSearch((current) => ({ ...current, offset: prevOffset(current.offset) }));
    },

    goNext: () => {
      setSearch((current) => ({
        ...current,
        offset: nextOffset(current.offset, results?.total ?? 0),
      }));
    },
  };
}
